import { useCallback, useEffect, useState } from "react";
import { url, ak } from "./HomePage";

function formatDateTime(value) {
  if (!value) return "–";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return new Intl.DateTimeFormat("fi-FI", {
    timeZone: "Europe/Helsinki",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function ShiftProposalsPage() {
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [processingId, setProcessingId] = useState(null);
  const [rejectingProposal, setRejectingProposal] = useState(null);
  const [rejectionReason, setRejectionReason] = useState("");
  const [editingProposal, setEditingProposal] = useState(null);
  const [editStartTime, setEditStartTime] = useState("");
  const [editEndTime, setEditEndTime] = useState("");
  const [editReason, setEditReason] = useState("");


  const loadProposals = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const response = await fetch(url + "/admin/shift-proposals" + ak, {
        method: "GET",
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error("Ehdotusten lataaminen epäonnistui.");
      }

      const data = await response.json();
      setProposals(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || "Ehdotusten lataaminen epäonnistui.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProposals();
  }, [loadProposals]);

  async function reviewProposal(id, action, reason = "") {
    const isApproval = action === "approve";

    if (
      isApproval &&
      !window.confirm("Hyväksytäänkö tämä käyntiehdotus uudeksi työkeikaksi?")
    ) {
      return;
    }

    const trimmedReason = reason.trim();
    if (!isApproval && !trimmedReason) {
      return;
    }

    setProcessingId(id);
    setError("");

    try {
      const response = await fetch(
        url + `/admin/shift-proposals/${id}/${action}` + ak,
        {
          method: "POST",
          credentials: "include",
          headers: !isApproval
            ? { "Content-Type": "application/json" }
            : undefined,
          body: !isApproval
            ? JSON.stringify({ reason: trimmedReason })
            : undefined,
        }
      );

      if (!response.ok) {
        throw new Error(
          isApproval
            ? "Ehdotuksen hyväksyminen epäonnistui."
            : "Ehdotuksen hylkääminen epäonnistui."
        );
      }

      setRejectingProposal(null);
      setRejectionReason("");
      await loadProposals();
    } catch (err) {
      setError(err.message || "Toiminto epäonnistui.");
    } finally {
      setProcessingId(null);
    }
  }

  return (
    <div className="w-full min-h-[90%] flex justify-center pt-16 pb-12">
      <div className="w-11/12 max-w-6xl">
        <div className="w-full h-auto rounded-lg bg-white p-6 shadow-md">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
            <div>
              <h1 className="text-3xl font-bold">Käyntiehdotukset</h1>
              <p className="mt-1 text-gray-600">
                Työntekijöiden ehdottamat uudet käynnit odottavat hyväksyntää.
              </p>
            </div>

            <button
              type="button"
              className="bg-main font-bold text-white rounded px-5 h-10 drop-shadow-md"
              onClick={loadProposals}
              disabled={loading}
            >
              Päivitä
            </button>
          </div>

          {error && (
            <div className="mb-5 rounded border border-red-300 bg-red-50 p-4 text-red-800">
              {error}
            </div>
          )}

          {loading ? (
            <p>Ladataan käyntiehdotuksia...</p>
          ) : proposals.length === 0 ? (
            <div className="rounded border border-gray-200 bg-white p-6">
              <p className="font-semibold">Ei odottavia käyntiehdotuksia.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {proposals.map((proposal) => {
                const busy = processingId === proposal.id;

                return (
                  <div
                    key={proposal.id}
                    className="rounded-lg border-2 border-orange-300 bg-orange-50 p-5 drop-shadow-sm"
                  >
                    <div className="grid gap-3 md:grid-cols-2">
                      <div>
                        <p className="text-sm text-gray-500">Työntekijä</p>
                        <p className="font-semibold">
                          {proposal.user_fullname || `Työntekijä #${proposal.user_id}`}
                        </p>
                      </div>

                      <div>
                        <p className="text-sm text-gray-500">Työkeikka</p>
                        <p className="font-semibold">
                          {proposal.source_customer ||
                            proposal.source_private_title ||
                            `Keikka #${proposal.source_gig_id}`}
                        </p>
                      </div>

                      <div>
                        <p className="text-sm text-gray-500">
                          {proposal.admin_start ? "Hyväksyttävä alku" : "Alkaa"}
                        </p>
                        <p>
                          {formatDateTime(
                            proposal.admin_start || proposal.proposed_start
                          )}
                        </p>
                      </div>

                      <div>
                        <p className="text-sm text-gray-500">
                          {proposal.admin_end ? "Hyväksyttävä loppu" : "Päättyy"}
                        </p>
                        <p>
                          {formatDateTime(
                            proposal.admin_end || proposal.proposed_end
                          )}
                        </p>
                      </div>
                    </div>

                    {(proposal.admin_start || proposal.admin_end) && (
                      <div className="mt-4 rounded border border-orange-300 bg-white p-3">
                        <p className="font-semibold">Admin muokkasi ehdotusta</p>
                        <p className="mt-1 text-sm text-gray-600">
                          Alkuperäinen ehdotus:{" "}
                          {formatDateTime(proposal.proposed_start)} –{" "}
                          {formatDateTime(proposal.proposed_end)}
                        </p>
                        {proposal.admin_edit_reason && (
                          <div className="mt-2">
                            <p className="text-sm text-gray-500">Muutoksen syy</p>
                            <p className="whitespace-pre-wrap">
                              {proposal.admin_edit_reason}
                            </p>
                          </div>
                        )}
                      </div>
                    )}

                    {proposal.note && (
                      <div className="mt-4">
                        <p className="text-sm text-gray-500">Työntekijän huomio</p>
                        <p className="whitespace-pre-wrap">{proposal.note}</p>
                      </div>
                    )}

                    <div className="mt-5 flex flex-wrap gap-8">
                      <button
                        type="button"
                        className="rounded bg-main px-5 py-2 font-bold text-white disabled:opacity-50"
                        disabled={busy || processingId !== null}
                        onClick={() => reviewProposal(proposal.id, "approve")}
                      >
                        {busy ? "Käsitellään..." : "Hyväksy"}
                      </button>

                      <button
                        type="button"
                        className="rounded border border-gray-400 bg-white px-5 py-2 font-bold text-gray-800 disabled:opacity-50"
                        disabled={busy || processingId !== null}
                        onClick={() => {
                          const start = proposal.admin_start || proposal.proposed_start;
                          const end = proposal.admin_end || proposal.proposed_end;

                          setEditingProposal(proposal);
                          setEditStartTime(start ? start.slice(0, 16) : "");
                          setEditEndTime(end ? end.slice(0, 16) : "");
                          setEditReason(proposal.admin_edit_reason || "");
                        }}
                      >
                        ✏ Muokkaa
                      </button>

                      <button
                        type="button"
                        className="rounded bg-red-700 px-5 py-2 font-bold text-white disabled:opacity-50"
                        disabled={busy || processingId !== null}
                        onClick={() => {
                          setRejectingProposal(proposal);
                          setRejectionReason("");
                        }}
                      >
                        Hylkää
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {editingProposal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4">
          <div className="w-full max-w-lg rounded bg-white p-6 shadow-xl">
            <h2 className="text-xl font-bold">Muokkaa käyntiehdotusta</h2>
            <p className="mt-2 text-gray-600">
              Muuta tarvittaessa päivämäärää tai aikaa. Muutoksen syy on pakollinen.
            </p>

            <div className="mt-5">
              <label className="block text-sm font-bold">Alkaa</label>
              <input
                type="datetime-local"
                className="mt-1 w-full rounded border border-gray-300 p-3"
                value={editStartTime}
                onChange={(event) => setEditStartTime(event.target.value)}
              />
            </div>

            <div className="mt-4">
              <label className="block text-sm font-bold">Päättyy</label>
              <input
                type="datetime-local"
                className="mt-1 w-full rounded border border-gray-300 p-3"
                value={editEndTime}
                onChange={(event) => setEditEndTime(event.target.value)}
              />
            </div>

            <div className="mt-4">
              <label className="block text-sm font-bold">Muutoksen syy</label>
              <textarea
                className="mt-1 min-h-[100px] w-full rounded border border-gray-300 p-3"
                value={editReason}
                onChange={(event) => setEditReason(event.target.value)}
                maxLength={500}
                placeholder="Esim. asiakkaan saldo riittää kolmeen tuntiin."
              />
              <div className="mt-1 text-right text-sm text-gray-500">
                {editReason.length}/500
              </div>
            </div>

            <div className="mt-5 flex justify-end gap-3">
              <button
                type="button"
                className="rounded border border-gray-300 px-5 py-2 font-bold"
                disabled={processingId !== null}
                onClick={() => {
                  setEditingProposal(null);
                  setEditStartTime("");
                  setEditEndTime("");
                  setEditReason("");
                }}
              >
                Peruuta
              </button>

              <button
                type="button"
                className="rounded bg-main px-5 py-2 font-bold text-white disabled:opacity-50"
                disabled={
                  processingId !== null ||
                  !editStartTime ||
                  !editEndTime ||
                  !editReason.trim()
                }
                onClick={async () => {
                  setProcessingId(editingProposal.id);
                  setError("");

                  try {
                    const response = await fetch(
                      url +
                        `/admin/shift-proposals/${editingProposal.id}/edit` +
                        ak,
                      {
                        method: "POST",
                        credentials: "include",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({
                          start_time: editStartTime,
                          end_time: editEndTime,
                          reason: editReason.trim(),
                        }),
                      }
                    );

                    if (!response.ok) {
                      throw new Error("Ehdotuksen muokkaaminen epäonnistui.");
                    }

                    setEditingProposal(null);
                    setEditStartTime("");
                    setEditEndTime("");
                    setEditReason("");
                    await loadProposals();
                  } catch (err) {
                    setError(err.message || "Ehdotuksen muokkaaminen epäonnistui.");
                  } finally {
                    setProcessingId(null);
                  }
                }}
              >
                {processingId === editingProposal.id
                  ? "Tallennetaan..."
                  : "Tallenna muutos"}
              </button>
            </div>
          </div>
        </div>
      )}

      {rejectingProposal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4">
          <div className="w-full max-w-lg rounded bg-white p-6 shadow-xl">
            <h2 className="text-xl font-bold">Hylkää käyntiehdotus</h2>
            <p className="mt-2 text-gray-600">
              Kirjoita työntekijälle hylkäyksen syy.
            </p>

            <textarea
              className="mt-4 min-h-[120px] w-full rounded border border-gray-300 p-3"
              value={rejectionReason}
              onChange={(event) => setRejectionReason(event.target.value)}
              maxLength={500}
              placeholder="Esim. asiakkaalla ei ole viikonloppuoikeutta."
              autoFocus
            />

            <div className="mt-1 text-right text-sm text-gray-500">
              {rejectionReason.length}/500
            </div>

            <div className="mt-5 flex justify-end gap-3">
              <button
                type="button"
                className="rounded border border-gray-300 px-5 py-2 font-bold"
                disabled={processingId !== null}
                onClick={() => {
                  setRejectingProposal(null);
                  setRejectionReason("");
                }}
              >
                Peruuta
              </button>

              <button
                type="button"
                className="rounded bg-red-700 px-5 py-2 font-bold text-white disabled:opacity-50"
                disabled={
                  processingId !== null || rejectionReason.trim().length === 0
                }
                onClick={() =>
                  reviewProposal(
                    rejectingProposal.id,
                    "reject",
                    rejectionReason
                  )
                }
              >
                {processingId === rejectingProposal.id
                  ? "Hylätään..."
                  : "Hylkää ehdotus"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ShiftProposalsPage;
